import { Button, DynamicContainer, Form, FormContext } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import React, { Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import { triggerEvent } from 'src/js/events';
import { useStores } from 'src/js/hook/use-stores';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { submitAndValidate, validateFormContext } from 'src/js/validator/validator';
import { St2110Type, St2110ViewModel, st2110ValidationSchema } from './st2110-models';
import st2110Models, { createSt2110, updateSt2110 } from './st2110-store';
import { useMutable } from 'src/js/hook/use-mutable';

interface Props {
  model: St2110ViewModel;
  onCancel?: VoidFunction;
  nmosServiceActive: boolean;
}

export interface St2110AddFormRef {
  submitAddSt2110: () => Promise<void>;
}

export const St2110Edit = forwardRef((props: Props, ref: Ref<St2110AddFormRef>) => {
  const { model, onCancel } = props;
  const { t } = useTranslation();
  const { sessionStore } = useStores();
  const nmosServiceActive = props.nmosServiceActive;
  const formRef = useRef<FormikProps<St2110ViewModel>>();
  const [settings, setSettings] = useState<St2110ViewModel>(model);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const nameField = useRef<HTMLInputElement>();
  const [packetTimeWasTweaked, setPacketTimeWasTweaked] = useMutable(false);

  if (settings.packetTimeActual !== model.packetTimeActual) {
    // allow live update for the actual value only
    if (formRef.current && formRef.current) {
      formRef.current.initialValues.packetTimeActual = model.packetTimeActual;
    }
  }

  const packetTimeOptions = [
    { value: 1, label: '1 ms', disabled: false },
    { value: 2, label: '125 µs' },
  ];

  const getRTPPayloadDefault = (type: string) => {
    switch (type) {
      case St2110Type.video:
        return 96;
      case St2110Type.audio:
        return 98;
      case St2110Type.data:
        return 100;
      default:
        return 0;
    }
  };

  const newSt2110 = settings.id === -1;

  useEffect(() => {
    if (newSt2110) {
      nameField.current?.focus?.(); // this wont work in list panel
    }
  }, []);

  const handleSubmit = (values: St2110ViewModel) => {
    setButtonState('pending');

    const data = {
      ...values,
      rtpPayload: isNilOrEmpty(values.rtpPayload.toString()) ? getRTPPayloadDefault(values.type) : values.rtpPayload,
    };

    if (newSt2110) {
      return createSt2110(data, t).then((res) => {
        setButtonState(undefined);
        if (res.ok) {
          triggerEvent('preset-update');
          setPacketTimeWasTweaked(false);
        }
        return res;
      });
    } else {
      return updateSt2110(data, t).then((res) => {
        if (res.ok) {
          const essense = st2110Models.find((e) => e.id === model.id);
          setSettings(essense); // update edit view with newest model
          formRef.current.resetForm({ values: essense });
          triggerEvent('preset-update');
          setPacketTimeWasTweaked(false);
        }
        setButtonState(undefined);
        return res;
      });
    }
  };

  const submitAddSt2110 = () => {
    return submitAndValidate(formRef.current, t);
  };

  useImperativeHandle(ref, () => ({
    submitAddSt2110,
  }));

  /*
  if (isNil(inputChannelOptions)) {
    return null;
  }*/

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit as any}
      className="hai-mt-n8"
      restValidationProps={{
        enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
        validationSchema: st2110ValidationSchema(t),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(genericContext) => {
          const formContext: FormikProps<St2110ViewModel> = genericContext as any;
          const values = formContext.values;

          let packetTimeActual = undefined;
          packetTimeOptions[0].disabled = false;
          if (
            formContext.initialValues['packetTime'] !== formContext.initialValues['packetTimeActual'] &&
            formContext.initialValues['packetTimeActual'] != null
          ) {
            packetTimeActual = values.packetTimeActual === 2 ? packetTimeOptions[1].label : undefined;
            if (values.packetTimeActual === 2) {
              packetTimeOptions[0].disabled = true;
            }
          }
          // console.log('packetTimeActual', packetTimeActual);
          /*
          if (values.numOfChannels > 10) {
            packetTimeOptions[0].disabled = true;
            if (newSt2110 && packetTimeWasTweaked() === false && values.packetTime !== 2) {
              formContext.setFieldValue('packetTime', 2);
              formContext.setFieldTouched('packetTime');
              setPacketTimeWasTweaked(true);
            }  
          }  else if (values.numOfChannels <= 10 && packetTimeWasTweaked() === true && values.packetTime === 2) {
              formContext.setFieldValue('packetTime', 1);
              formContext.setFieldTouched('packetTime');
              setPacketTimeWasTweaked(false); // put back to default behavior
          }
          */
          return (
            <>
              <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
                <FormTextfield
                  name="name"
                  inputRef={nameField}
                  mask
                  title={t('general.name')}
                  placeholder={t('st2110.name', {
                    output: values.output,
                    type: t(`st2110.mediaTypesName.${values.type}`),
                  })}
                  className="max-width"
                  autoFocus={newSt2110}
                  disabled={nmosServiceActive}
                />
                {!newSt2110 && !sessionStore.isUser() && (
                  <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                    <Button className="hai-mr-6" onClick={onCancel}>
                      {t('general.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => validateFormContext(formContext, t)}
                      disabled={!formContext.dirty || nmosServiceActive}
                      state={buttonState}
                    >
                      {t('general.apply')}
                    </Button>
                  </div>
                )}
              </DynamicContainer>
              <FormBuilder
                translationPrefixLabels="st2110.fields"
                translationPrefixEnums="st2110.enums"
                layout={FormLayout.THREE_COLUMNS_GRID}
                fields={[
                  {
                    type: FormFieldType.TITLE,
                    label: t('st2110.sections.st2110'),
                    className: 'hai-mb-0',
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.STATIC_TEXT,
                    name: 'type',
                    value: t(`st2110.mediaTypes.${values.type}`),
                  },
                  {
                    type: FormFieldType.CHECKBOX,
                    name: 'autoStart',
                    when: newSt2110,
                    disabled: nmosServiceActive,
                  },
                  /* Not needed on decoder, we follow what we receive from the decoder
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'numOfChannels',
                    hint: t('st2110.fields.numOfChannelsExplain'),
                    when: values.type === St2110Type.audio,
                    required: true,
                    disabled: nmosServiceActive,
                  },*/
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'packetTime',
                    options: packetTimeOptions,
                    when: values.type === St2110Type.audio,
                    disabled: nmosServiceActive,
                    hint:
                      packetTimeActual != null
                        ? t('st2110.fields.packetTimeMismatch', { actual: packetTimeActual })
                        : undefined,
                    onChange: () => {
                      setPacketTimeWasTweaked(false);
                    },
                  },
                  {
                    type: FormFieldType.TITLE,
                    label: t('st2110.sections.settings'),
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'address',
                    required: true,
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'port',
                    required: true,
                    hint: t('st2110.fields.portExplain'),
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'rtpPayload',
                    placeholder: getRTPPayloadDefault(values.type).toString(),
                    hint: t('st2110.fields.rtpPayloadExplain'),
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'tos',
                    hint: t('st2110.fields.tosExplain'),
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'ttl',
                    hint: t('st2110.fields.ttlExplain'),
                    disabled: nmosServiceActive,
                  },
                ]}
              />
            </>
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
});
St2110Edit.displayName = 'St2110Edit';
